import * as XLSX from 'xlsx';

// Helper to format kenmerk for display
const formatKenmerkForExport = (kenmerk) => {
  if (!kenmerk) return '';
  const clean = kenmerk.replace(/\s/g, '');
  // Don't format aanslagnummers (contain letters)
  if (/[A-Za-z]/.test(clean)) return clean;
  return clean.replace(/(\d{4})/g, '$1 ').trim();
};

// Generate filename with date
const generateFilename = (base, extension) => {
  const now = new Date();
  const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
  return `${base}_${dateStr}.${extension}`;
};

// Get input type label
const getTypeLabel = (result, lang = 'nl') => {
  if (result.input_type === 'aanslagnummer') {
    return lang === 'nl' ? 'Aanslagnummer' : 'Assessment';
  }
  return lang === 'nl' ? 'Betalingskenmerk' : 'Payment Ref';
};

export const exportToCSV = (results, filename = null, language = 'nl') => {
  const finalFilename = filename || generateFilename('kenmerkcheck_export', 'csv');
  
  const headers = [
    'Kenmerk',
    'Geformatteerd', 
    'Type',
    'Geldig', 
    'Belastingtype', 
    'Code',
    'Jaar', 
    'Tijdvak', 
    'Tijdvak Type',
    'Aanslagnummer', 
    'BSN/RSIN (gemaskeerd)',
    'Foutmelding'
  ];
  
  const rows = results.map(r => [
    r.kenmerk || '',
    formatKenmerkForExport(r.kenmerk),
    getTypeLabel(r, language),
    r.valid ? 'Ja' : 'Nee',
    r.belastingtype?.nl || r.belastingtype?.en || '',
    r.belastingcode || '',
    r.jaar || '',
    r.tijdvak || '',
    r.tijdvak_type?.nl || r.tijdvak_type?.en || '',
    r.aanslagnummer || '',
    r.identificatie || '',
    r.error || ''
  ]);
  
  // Add summary row
  const validCount = results.filter(r => r.valid).length;
  const invalidCount = results.length - validCount;
  rows.push([]);
  rows.push([`Totaal: ${results.length}`, '', '', `Geldig: ${validCount}`, `Ongeldig: ${invalidCount}`, '', '', '', '', '', '', '']);
  rows.push([`Geëxporteerd: ${new Date().toLocaleString('nl-NL')}`]);
  
  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
  ].join('\n');
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = finalFilename;
  link.click();
  URL.revokeObjectURL(link.href);
};

export const exportToExcel = (results, filename = null, language = 'nl') => {
  const finalFilename = filename || generateFilename('kenmerkcheck_export', 'xlsx');
  
  // Main data sheet
  const data = results.map(r => ({
    'Kenmerk': r.kenmerk || '',
    'Geformatteerd': formatKenmerkForExport(r.kenmerk),
    'Type': getTypeLabel(r, language),
    'Geldig': r.valid ? 'Ja' : 'Nee',
    'Belastingtype': r.belastingtype?.nl || r.belastingtype?.en || '',
    'Code': r.belastingcode || '',
    'Jaar': r.jaar || '',
    'Tijdvak': r.tijdvak || '',
    'Tijdvak Type': r.tijdvak_type?.nl || r.tijdvak_type?.en || '',
    'Aanslagnummer': r.aanslagnummer || '',
    'BSN/RSIN': r.identificatie || '',
    'Foutmelding': r.error || ''
  }));
  
  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // Set column widths
  worksheet['!cols'] = [
    { wch: 18 },  // Kenmerk
    { wch: 22 },  // Geformatteerd
    { wch: 14 },  // Type
    { wch: 8 },   // Geldig
    { wch: 30 },  // Belastingtype
    { wch: 8 },   // Code
    { wch: 8 },   // Jaar
    { wch: 12 },  // Tijdvak
    { wch: 12 },  // Tijdvak Type
    { wch: 20 },  // Aanslagnummer
    { wch: 14 },  // BSN/RSIN
    { wch: 25 },  // Foutmelding
  ];
  
  // Create summary sheet
  const validCount = results.filter(r => r.valid).length;
  const invalidCount = results.length - validCount;
  
  // Group by tax type
  const taxTypeCounts = {};
  results.forEach(r => {
    if (r.valid && r.belastingtype?.nl) {
      const type = r.belastingtype.nl;
      taxTypeCounts[type] = (taxTypeCounts[type] || 0) + 1;
    }
  });
  
  // Group by year
  const yearCounts = {};
  results.forEach(r => {
    if (r.valid && r.jaar) {
      yearCounts[r.jaar] = (yearCounts[r.jaar] || 0) + 1;
    }
  });
  
  const summaryData = [
    { 'Samenvatting': 'KenmerkCheck Export', 'Waarde': '' },
    { 'Samenvatting': '', 'Waarde': '' },
    { 'Samenvatting': 'Totaal kenmerken', 'Waarde': results.length },
    { 'Samenvatting': 'Geldig', 'Waarde': validCount },
    { 'Samenvatting': 'Ongeldig', 'Waarde': invalidCount },
    { 'Samenvatting': 'Succespercentage', 'Waarde': `${Math.round((validCount / results.length) * 100)}%` },
    { 'Samenvatting': '', 'Waarde': '' },
    { 'Samenvatting': 'Per belastingtype:', 'Waarde': '' },
    ...Object.entries(taxTypeCounts).map(([type, count]) => ({
      'Samenvatting': `  ${type}`,
      'Waarde': count
    })),
    { 'Samenvatting': '', 'Waarde': '' },
    { 'Samenvatting': 'Per jaar:', 'Waarde': '' },
    ...Object.entries(yearCounts).sort((a, b) => b[0] - a[0]).map(([year, count]) => ({
      'Samenvatting': `  ${year}`,
      'Waarde': count
    })),
    { 'Samenvatting': '', 'Waarde': '' },
    { 'Samenvatting': 'Geëxporteerd op', 'Waarde': new Date().toLocaleString('nl-NL') },
  ];
  
  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  summarySheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
  
  // Create workbook with multiple sheets
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Kenmerken');
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Samenvatting');
  
  // Only valid results sheet
  if (validCount > 0) {
    const validData = results
      .filter(r => r.valid)
      .map(r => ({
        'Kenmerk': formatKenmerkForExport(r.kenmerk),
        'Type': getTypeLabel(r, language),
        'Belastingtype': r.belastingtype?.nl || '',
        'Jaar': r.jaar || '',
        'Tijdvak': r.tijdvak || '',
      }));
    const validSheet = XLSX.utils.json_to_sheet(validData);
    validSheet['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 30 }, { wch: 8 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(workbook, validSheet, 'Alleen Geldig');
  }
  
  // Invalid results sheet (if any)
  if (invalidCount > 0) {
    const invalidData = results
      .filter(r => !r.valid)
      .map(r => ({
        'Kenmerk': r.kenmerk || '',
        'Foutmelding': r.error || 'Onbekende fout',
      }));
    const invalidSheet = XLSX.utils.json_to_sheet(invalidData);
    invalidSheet['!cols'] = [{ wch: 22 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(workbook, invalidSheet, 'Ongeldige');
  }
  
  XLSX.writeFile(workbook, finalFilename);
};

// Export only valid results
export const exportValidOnly = (results, format = 'csv', language = 'nl') => {
  const validResults = results.filter(r => r.valid);
  const filename = generateFilename('kenmerkcheck_geldig', format === 'csv' ? 'csv' : 'xlsx');
  
  if (format === 'csv') {
    exportToCSV(validResults, filename, language);
  } else {
    exportToExcel(validResults, filename, language);
  }
};

// Export for specific tax type
export const exportByTaxType = (results, taxType, format = 'csv', language = 'nl') => {
  const filtered = results.filter(r => 
    r.valid && (r.belastingtype?.nl === taxType || r.belastingtype?.en === taxType)
  );
  const safeTaxType = taxType.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  const filename = generateFilename(`kenmerkcheck_${safeTaxType}`, format === 'csv' ? 'csv' : 'xlsx');
  
  if (format === 'csv') {
    exportToCSV(filtered, filename, language);
  } else {
    exportToExcel(filtered, filename, language);
  }
};
